/**
 * Editor de automação do Instagram — página própria.
 *
 * Esquerda: o formulário em seções, na ordem em que a pessoa pensa (onde
 * dispara → o que dispara → o que a pessoa recebe → extras). Direita: uma
 * prévia fixa do celular mostrando a conversa que a automação vai produzir,
 * atualizando enquanto se digita. A prévia usa a mesma substituição de
 * `{nome}`/`{link}` do servidor, então o que se vê é o que sai.
 *
 * Rotas: `/instagram/automations/new` (aceita `?modelo=<slug>`) e
 * `/instagram/automations/:id`.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import InstagramAccountSelect, { useInstagramAccounts } from "@/components/integrations/InstagramAccountSelect";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Check,
  ExternalLink,
  Film,
  Grid3X3,
  Image as ImageIcon,
  Loader2,
  MessageCircle,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { casarPalavrasChave, normalizarListaDeChaves } from "@shared/instagram-keywords";
import { INSTAGRAM_AUTOMATION_TEMPLATES } from "@shared/instagram-automation-templates";
import {
  FORM_VAZIO,
  ROTULO_PADRAO_SEGUIR,
  TEXTO_PADRAO_SEGUIR,
  deLinha,
  deModelo,
  paraEnvio,
  renderizarPrevia,
  type Alvo,
  type AutomationForm,
} from "@/lib/instagram-automation-form";

// ─── Peças do formulário ─────────────────────────────────────────────────────

function Secao({
  numero,
  titulo,
  descricao,
  children,
}: {
  numero: number;
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {numero}
          </span>
          {titulo}
        </CardTitle>
        {descricao && <CardDescription>{descricao}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Campo({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Extra({
  titulo,
  descricao,
  ligado,
  onChange,
  children,
}: {
  titulo: string;
  descricao: string;
  ligado: boolean;
  onChange: (v: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <Card className={ligado ? "border-primary/40" : ""}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{titulo}</p>
            <p className="text-xs text-muted-foreground">{descricao}</p>
          </div>
          <Switch checked={ligado} onCheckedChange={onChange} />
        </div>
        {ligado && children}
      </CardContent>
    </Card>
  );
}

function OpcaoAlvo({
  ativo,
  icon,
  titulo,
  descricao,
  onClick,
}: {
  ativo: boolean;
  icon: React.ReactNode;
  titulo: string;
  descricao: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-start gap-3 rounded-lg border p-3 text-left transition ${
        ativo ? "border-primary bg-primary/5" : "hover:border-muted-foreground/40"
      }`}
    >
      <span className={`mt-0.5 ${ativo ? "text-primary" : "text-muted-foreground"}`}>{icon}</span>
      <span>
        <span className="block text-sm font-medium">{titulo}</span>
        <span className="block text-xs text-muted-foreground">{descricao}</span>
      </span>
    </button>
  );
}

/** Grade de posts com busca por legenda e "carregar mais". */
function SeletorDePost({ form, setForm }: { form: AutomationForm; setForm: (f: AutomationForm) => void }) {
  const [busca, setBusca] = useState("");
  const utils = trpc.useUtils();
  const contas = useInstagramAccounts();
  // Com várias contas, os posts são os da conta escolhida — e sem escolha não
  // há o que listar (o servidor recusaria presumir).
  const precisaEscolher = (contas.data?.length ?? 0) > 1 && form.instagramAccountId == null;
  const accountId = form.instagramAccountId ?? undefined;
  const posts = trpc.instagram.media.list.useQuery(
    { limit: 24, accountId },
    { staleTime: 60_000, enabled: !precisaEscolher },
  );
  // Paginação por cursor à mão: o procedure usa `after`, e o useInfiniteQuery
  // do tRPC exige um input chamado `cursor`.
  const [extras, setExtras] = useState<NonNullable<typeof posts.data>["items"]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const proximoCursor = cursor === undefined ? posts.data?.nextCursor ?? null : cursor;
  const carregarMais = async () => {
    if (!proximoCursor) return;
    setCarregandoMais(true);
    try {
      const r = await utils.instagram.media.list.fetch({ limit: 24, after: proximoCursor, accountId });
      setExtras((e) => [...e, ...r.items]);
      setCursor(r.nextCursor);
    } catch (err) {
      toast.error(toUserFriendlyError(err));
    } finally {
      setCarregandoMais(false);
    }
  };
  const todos = useMemo(() => [...(posts.data?.items ?? []), ...extras], [posts.data, extras]);
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return todos;
    return todos.filter((m) => (m.caption ?? "").toLowerCase().includes(q));
  }, [todos, busca]);
  const selecionado = todos.find((m) => m.id === form.mediaId);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar pela legenda…" className="pl-9" />
      </div>
      {precisaEscolher && (
        <p className="text-xs text-muted-foreground">Escolha a conta no topo da página para ver os posts dela.</p>
      )}
      {posts.isLoading && !precisaEscolher && <p className="text-xs text-muted-foreground">Carregando posts…</p>}
      {posts.isError && <p className="text-xs text-red-600">{toUserFriendlyError(posts.error)}</p>}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {filtrados.map((m) => {
          const sel = form.mediaId === m.id;
          const thumb = m.thumbnailUrl ?? (m.mediaType === "VIDEO" ? null : m.mediaUrl);
          return (
            <button
              key={m.id}
              type="button"
              title={m.caption ?? ""}
              onClick={() => setForm({ ...form, mediaId: m.id, mediaPermalink: m.permalink, mediaThumb: thumb })}
              className={`relative aspect-square overflow-hidden rounded-md border bg-muted transition ${
                sel ? "ring-2 ring-primary ring-offset-2" : "hover:opacity-90"
              }`}
            >
              {thumb ? (
                <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <span className="flex h-full items-center justify-center text-[10px] text-muted-foreground">{m.mediaType}</span>
              )}
              {m.mediaProductType === "REELS" && (
                <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[9px] text-white">Reel</span>
              )}
              {m.commentsCount != null && (
                <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-[9px] text-white">💬 {m.commentsCount}</span>
              )}
              {sel && (
                <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-white">
                  <Check className="h-3 w-3" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between">
        {proximoCursor ? (
          <Button type="button" size="sm" variant="outline" onClick={() => void carregarMais()} disabled={carregandoMais}>
            {carregandoMais ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
            Carregar mais posts
          </Button>
        ) : (
          <span />
        )}
        {(selecionado?.permalink || form.mediaPermalink) && (
          <a
            href={selecionado?.permalink ?? form.mediaPermalink ?? "#"}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> Ver post selecionado
          </a>
        )}
      </div>
      {selecionado?.caption && (
        <p className="line-clamp-2 rounded bg-muted/50 p-2 text-xs text-muted-foreground">{selecionado.caption}</p>
      )}
    </div>
  );
}

// ─── Prévia ──────────────────────────────────────────────────────────────────

function Bolha({ de, children, botoes }: { de: "eles" | "nos"; children: React.ReactNode; botoes?: string[] }) {
  const nos = de === "nos";
  return (
    <div className={`flex ${nos ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%] space-y-1">
        <div
          className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-[13px] leading-snug ${
            nos ? "rounded-br-sm bg-[#3797f0] text-white" : "rounded-bl-sm bg-neutral-200 text-neutral-900 dark:bg-neutral-700 dark:text-neutral-100"
          }`}
        >
          {children}
        </div>
        {botoes?.map((b) => (
          <div key={b} className="rounded-xl border border-[#3797f0] bg-white px-3 py-1.5 text-center text-[13px] font-medium text-[#3797f0] dark:bg-neutral-900">
            {b || "Botão"}
          </div>
        ))}
      </div>
    </div>
  );
}

function Previa({ form, exemplo }: { form: AutomationForm; exemplo: string }) {
  const nome = "maria";
  const chaves = normalizarListaDeChaves(form.keywordsTexto);
  const comentario = exemplo.trim() || chaves[0] || "…";
  const temLink = !!form.link1.trim();
  const usaAbertura = form.openingDmEnabled && form.openingDmMessage.trim() && form.openingDmButtonLabel.trim();
  const respostasPublicas = form.publicReplyEnabled
    ? form.publicReplyTexto.split("\n").map((s) => s.trim()).filter(Boolean)
    : [];
  const botoesLink = temLink
    ? [form.linkButtonLabel.trim() || "Abrir link", ...(form.link2.trim() ? [form.link2Label.trim() || "Abrir link"] : [])]
    : [];
  const textoEntrega = renderizarPrevia(form.dmMessage, nome) || (temLink ? "Aqui está o seu link:" : "");

  return (
    <div className="space-y-3">
      <div className="mx-auto w-full max-w-[320px] overflow-hidden rounded-[28px] border-[6px] border-neutral-900 bg-white shadow-xl dark:bg-neutral-950">
        <div className="flex items-center gap-2 border-b px-3 py-2 text-xs">
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-pink-500 to-yellow-400" />
          <span className="font-semibold">sua_conta</span>
          <span className="ml-auto text-muted-foreground">Instagram</span>
        </div>

        {/* Post + comentário */}
        <div className="space-y-2 border-b p-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <MessageCircle className="h-3 w-3" /> Comentários
          </div>
          {form.alvo === "post" && form.mediaThumb && (
            <img src={form.mediaThumb} alt="" className="h-20 w-full rounded object-cover" />
          )}
          <div className="text-[13px]">
            <span className="font-semibold">@{nome}</span> {comentario}
          </div>
          {respostasPublicas[0] && (
            <div className="ml-4 text-[13px]">
              <span className="font-semibold">sua_conta</span> {renderizarPrevia(respostasPublicas[0], nome)}
              {respostasPublicas.length > 1 && (
                <span className="ml-1 text-[10px] text-muted-foreground">(+{respostasPublicas.length - 1} variações)</span>
              )}
            </div>
          )}
        </div>

        {/* DM */}
        <div className="space-y-2 p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Direct · @{nome}</div>
          {usaAbertura ? (
            <>
              <Bolha de="nos" botoes={[form.openingDmButtonLabel]}>{renderizarPrevia(form.openingDmMessage, nome)}</Bolha>
              <div className="text-center text-[10px] text-muted-foreground">— ao tocar no botão —</div>
            </>
          ) : null}
          {form.requireFollow ? (
            <>
              <Bolha de="nos" botoes={[form.followPromptButtonLabel || ROTULO_PADRAO_SEGUIR]}>
                {renderizarPrevia(form.followPromptMessage || TEXTO_PADRAO_SEGUIR, nome)}
              </Bolha>
              <div className="text-center text-[10px] text-muted-foreground">— quem segue e toca no botão —</div>
            </>
          ) : null}
          <Bolha de="nos" botoes={botoesLink}>{textoEntrega || <span className="opacity-60">Escreva a mensagem…</span>}</Bolha>
          {form.followUpEnabled && form.followUpMessage.trim() && (
            <>
              <div className="text-center text-[10px] text-muted-foreground">
                — {form.followUpDelayMinutes > 0 ? `${form.followUpDelayMinutes} min depois` : "logo em seguida"} —
              </div>
              <Bolha de="nos">{renderizarPrevia(form.followUpMessage, nome)}</Bolha>
            </>
          )}
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground">Prévia — o nome e o comentário são exemplos.</p>
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

function EditorContent({ id }: { id: number | null }) {
  const [, navigate] = useLocation();
  const search = useSearch();
  const utils = trpc.useUtils();

  const modeloSlug = new URLSearchParams(search).get("modelo");
  const modelo = INSTAGRAM_AUTOMATION_TEMPLATES.find((t) => t.slug === modeloSlug) ?? null;

  const existente = trpc.instagram.automations.get.useQuery({ id: id! }, { enabled: id != null, retry: false });

  const [form, setForm] = useState<AutomationForm>(() => (modelo ? deModelo(modelo) : FORM_VAZIO));
  const [carregado, setCarregado] = useState(id == null);
  const [exemplo, setExemplo] = useState("");
  const [sujo, setSujo] = useState(false);

  useEffect(() => {
    if (id != null && existente.data && !carregado) {
      setForm(deLinha(existente.data));
      setCarregado(true);
    }
  }, [id, existente.data, carregado]);

  useEffect(() => {
    if (!sujo) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [sujo]);

  const set = <K extends keyof AutomationForm>(k: K, v: AutomationForm[K]) => {
    setSujo(true);
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setTudo = (f: AutomationForm) => {
    setSujo(true);
    setForm(f);
  };

  const invalidar = () => {
    void utils.instagram.automations.list.invalidate();
    if (id != null) void utils.instagram.automations.get.invalidate({ id });
  };
  const criar = trpc.instagram.automations.create.useMutation({
    onSuccess: (r) => {
      invalidar();
      setSujo(false);
      toast.success("Automação criada.");
      navigate(`/instagram/automations/${r.id}`, { replace: true });
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const atualizar = trpc.instagram.automations.update.useMutation({
    onSuccess: () => {
      invalidar();
      setSujo(false);
      toast.success("Automação salva.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const salvando = criar.isPending || atualizar.isPending;

  const chaves = useMemo(() => normalizarListaDeChaves(form.keywordsTexto), [form.keywordsTexto]);
  const previaMatch = useMemo(
    () =>
      exemplo.trim()
        ? form.matchAnyWord
          ? { matched: true, matchedKeyword: null }
          : casarPalavrasChave(exemplo, chaves, form.wholeWordMatch)
        : null,
    [exemplo, chaves, form.matchAnyWord, form.wholeWordMatch],
  );

  const salvar = () => {
    const data = paraEnvio(form);
    if (id != null) atualizar.mutate({ id, data });
    else criar.mutate(data);
  };

  if (id != null && existente.isError) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-red-600">{toUserFriendlyError(existente.error)}</p>
        <Link href="/instagram/automations" className="mt-4 inline-block text-sm text-primary hover:underline">
          Voltar para as automações
        </Link>
      </div>
    );
  }
  if (!carregado) {
    return (
      <p className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/instagram/automations")}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Automações
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{id != null ? form.name || "Editar automação" : "Nova automação"}</h1>
            <p className="text-xs text-muted-foreground">Quem comentar a palavra-chave recebe a mensagem no privado.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Trocar de conta solta o post escolhido: ele é de outro perfil. */}
          <InstagramAccountSelect
            value={form.instagramAccountId}
            onChange={(v) => setTudo({ ...form, instagramAccountId: v, mediaId: "", mediaPermalink: null, mediaThumb: null })}
          />
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.isActive} onCheckedChange={(v) => set("isActive", v)} />
            {form.isActive ? "Ativa" : "Pausada"}
          </label>
          <Button onClick={salvar} disabled={salvando}>
            {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
            {id != null ? "Salvar" : "Criar automação"}
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Formulário */}
        <div className="space-y-5">
          {id == null && !modelo && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
              <Sparkles className="h-4 w-4" /> Começar por um modelo:
              {INSTAGRAM_AUTOMATION_TEMPLATES.map((t) => (
                <Button key={t.slug} size="sm" variant="outline" className="h-7 text-xs" onClick={() => setTudo(deModelo(t))}>
                  {t.title}
                </Button>
              ))}
            </div>
          )}

          <Secao numero={1} titulo="Nome">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex.: Link do e-book — reel de terça" maxLength={120} />
          </Secao>

          <Secao numero={2} titulo="Onde dispara" descricao="Em qual post os comentários vão acionar a automação.">
            <div className="flex flex-col gap-2 sm:flex-row">
              <OpcaoAlvo ativo={form.alvo === "post"} icon={<ImageIcon className="h-4 w-4" />} titulo="Um post específico" descricao="Escolha na grade abaixo." onClick={() => set("alvo", "post" as Alvo)} />
              <OpcaoAlvo ativo={form.alvo === "any"} icon={<Grid3X3 className="h-4 w-4" />} titulo="Qualquer post" descricao="Bom para “link”, “quero”." onClick={() => set("alvo", "any" as Alvo)} />
              <OpcaoAlvo ativo={form.alvo === "next_reel"} icon={<Film className="h-4 w-4" />} titulo="Próximo reel" descricao="Amarra sozinha ao próximo que publicar." onClick={() => set("alvo", "next_reel" as Alvo)} />
            </div>
            {form.alvo === "post" && (
              // `key` pela conta: a paginação guardada é dos posts de outro perfil.
              <SeletorDePost key={form.instagramAccountId ?? "unica"} form={form} setForm={setTudo} />
            )}
          </Secao>

          <Secao numero={3} titulo="Palavras-chave" descricao="Acentos, maiúsculas e emoji não importam: “PREÇO”, “preco” e “Preço!! 🔥” contam igual.">
            <Input value={form.keywordsTexto} onChange={(e) => set("keywordsTexto", e.target.value)} placeholder="link, quero, eu quero" disabled={form.matchAnyWord} />
            {chaves.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {chaves.map((k) => <Badge key={k} variant="secondary">{k}</Badge>)}
              </div>
            )}
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-xs">
                <Switch checked={form.matchAnyWord} onCheckedChange={(v) => set("matchAnyWord", v)} /> Qualquer comentário dispara
              </label>
              <label className="flex items-center gap-2 text-xs">
                <Switch checked={form.wholeWordMatch} onCheckedChange={(v) => set("wholeWordMatch", v)} disabled={form.matchAnyWord} /> Só palavra inteira (“link” não pega “linkado”)
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Input value={exemplo} onChange={(e) => setExemplo(e.target.value)} placeholder="Teste: como a pessoa comentaria?" className="h-9 text-sm" />
              {previaMatch && (
                <Badge variant={previaMatch.matched ? "default" : "outline"} className="whitespace-nowrap">
                  {previaMatch.matched ? <><Check className="mr-1 h-3 w-3" />dispara</> : <><X className="mr-1 h-3 w-3" />não dispara</>}
                </Badge>
              )}
            </div>
          </Secao>

          <Secao numero={4} titulo="O que a pessoa recebe" descricao="Use {nome} para o @ da pessoa. O link vai como botão; se preferir no texto, escreva {link}.">
            <Textarea value={form.dmMessage} onChange={(e) => set("dmMessage", e.target.value)} rows={3} maxLength={1000} />
            <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
              <Campo label="Link (rastreado)" hint="Cada clique conta. A pessoa recebe um endereço nosso que redireciona para cá.">
                <Input value={form.link1} onChange={(e) => set("link1", e.target.value)} placeholder="https://…" />
              </Campo>
              <Campo label="Texto do botão">
                <Input value={form.linkButtonLabel} onChange={(e) => set("linkButtonLabel", e.target.value)} maxLength={20} placeholder="Abrir link" />
              </Campo>
            </div>
            {form.link1.trim() && (
              <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
                <Campo label="Segundo link (opcional)">
                  <Input value={form.link2} onChange={(e) => set("link2", e.target.value)} placeholder="https://…" />
                </Campo>
                <Campo label="Texto do botão">
                  <Input value={form.link2Label} onChange={(e) => set("link2Label", e.target.value)} maxLength={20} placeholder="Ver mais" />
                </Campo>
              </div>
            )}
          </Secao>

          <div className="space-y-3">
            <h2 className="px-1 text-sm font-medium text-muted-foreground">Extras</h2>
            <Extra titulo="Responder também no post (público)" descricao="Um comentário abaixo do da pessoa, sorteado entre as opções — para não parecer robô." ligado={form.publicReplyEnabled} onChange={(v) => set("publicReplyEnabled", v)}>
              <Textarea value={form.publicReplyTexto} onChange={(e) => set("publicReplyTexto", e.target.value)} rows={3} placeholder="Uma opção por linha" />
            </Extra>
            <Extra titulo="Mensagem de abertura com botão" descricao="Em vez do link direto, a pessoa recebe um convite e toca no botão para receber. Abre a conversa e melhora a entrega." ligado={form.openingDmEnabled} onChange={(v) => set("openingDmEnabled", v)}>
              <Textarea value={form.openingDmMessage} onChange={(e) => set("openingDmMessage", e.target.value)} rows={2} maxLength={640} />
              <Campo label="Texto do botão">
                <Input value={form.openingDmButtonLabel} onChange={(e) => set("openingDmButtonLabel", e.target.value)} maxLength={20} placeholder="Quero o link" />
              </Campo>
            </Extra>
            <Extra titulo="Exigir que a pessoa siga a conta" descricao="Quem não segue recebe um pedido com botão “já estou seguindo”; o link só vai depois." ligado={form.requireFollow} onChange={(v) => set("requireFollow", v)}>
              <Textarea value={form.followPromptMessage} onChange={(e) => set("followPromptMessage", e.target.value)} rows={2} maxLength={640} placeholder={TEXTO_PADRAO_SEGUIR} />
              <Campo label="Texto do botão">
                <Input value={form.followPromptButtonLabel} onChange={(e) => set("followPromptButtonLabel", e.target.value)} maxLength={20} placeholder={ROTULO_PADRAO_SEGUIR} />
              </Campo>
            </Extra>
            <Extra titulo="Mensagem de acompanhamento" descricao="Alguns minutos depois de entregar o link, uma segunda mensagem (agradecer, perguntar se abriu)." ligado={form.followUpEnabled} onChange={(v) => set("followUpEnabled", v)}>
              <Textarea value={form.followUpMessage} onChange={(e) => set("followUpMessage", e.target.value)} rows={2} maxLength={1000} placeholder="Conseguiu abrir, {nome}? Qualquer dúvida é só responder aqui 🙂" />
              <div className="flex items-center gap-2 text-xs">
                Enviar após
                <Input type="number" min={0} max={1440} value={form.followUpDelayMinutes} onChange={(e) => set("followUpDelayMinutes", Math.max(0, Number(e.target.value) || 0))} className="h-8 w-20" />
                minutos
              </div>
            </Extra>
            <Extra titulo="Também disparar por DM" descricao="Se alguém mandar a palavra-chave no privado (ou respondendo a um story), responde do mesmo jeito." ligado={form.dmTriggerEnabled} onChange={(v) => set("dmTriggerEnabled", v)} />
          </div>

          <div className="flex gap-2 pb-8">
            <Button onClick={salvar} disabled={salvando}>
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              {id != null ? "Salvar" : "Criar automação"}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/instagram/automations")} disabled={salvando}>
              Cancelar
            </Button>
          </div>
        </div>

        {/* Prévia */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <Previa form={form} exemplo={exemplo} />
        </div>
      </div>
    </div>
  );
}

export default function InstagramAutomationEditor() {
  const params = useParams<{ id?: string }>();
  const id = params.id && params.id !== "new" ? Number(params.id) : null;
  return (
    <PermissionGuard permission="instagram:manage_automations">
      <DashboardLayout>
        <EditorContent key={id ?? "new"} id={Number.isInteger(id) ? id : null} />
      </DashboardLayout>
    </PermissionGuard>
  );
}
